import { html, nothing } from 'lit';
import alertError from '../../html/alert-error.js';
import { mapDeliveryDetails } from '../../lib/map-delivery-details.js';
import { request } from '../../lib/request.js';
import { BaseElement } from '../base.js';
import './modal-delivery-details.js';
import './modal-email.js';
import './modal-language.js';
import './modal-password.js';

const order_settings = [
  { id: 'allow_delivery_comments', name: 'Allow Delivery Comments' },
  { id: 'allow_product_comments', name: 'Allow Product Comments' }
];

export class ConnectAccountStatus extends BaseElement {
  static properties = {
    item: { state: true },
    updating: { state: true }
  };

  constructor() {
    super();
    this.item = null;
  }

  render() {
    if (!this.item) {
      return nothing;
    }
    const { item } = this;
    const is_active = item.status === 'ACTIVE';
    const { reset_password, email, lang } = item;
    const invite_link = reset_password
      ? `${location.protocol}//connect.yourbarmate.com/app/welcome?token=${reset_password.confirmation}&email=${email}&lang=${lang || 'de'}`
      : null;
    return html`
      ${is_active
        ? html`
            <div class="alert alert-success hstack gap-3">
              <div class="me-auto">
                <i class="bi bi-check2-circle me-1"></i>
                ACTIVE
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click=${this.onLogin}
              >
                Login
              </button>
            </div>
          `
        : alertError(item.status)}
      <div class="ms-3 mt-3 mb-4">
        ID: <shared-copy-id id=${item.account}></shared-copy-id>
      </div>
      ${reset_password && reset_password.type === 'INVITED'
        ? html`
            <div class="alert alert-warning my-4">
              Invite sent with link:
              <shared-copy-id id=${invite_link}></shared-copy-id>
            </div>
          `
        : nothing}
      <div class="row g-4">
        ${this.renderEmailCard()} ${this.renderPasswordCard()}
        ${this.renderTokenCard()} ${this.renderLanguageCard()}
        ${this.renderFlushCachesCard()} ${this.renderSettingsCard()}
        ${this.renderDeliveriesCard()}
      </div>
    `;
  }

  renderTokenCard() {
    const { token } = this.item;
    return html`
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card">
          <div class="card-header hstack">
            <h5 class="mb-0 me-auto">Token</h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              ?disabled=${this.updating}
              @click=${this.onResetToken}
            >
              Reset
            </button>
          </div>
          <div class="card-body">
            ${token
              ? html`<shared-copy-id id=${token}></shared-copy-id>`
              : '•••••'}
          </div>
        </div>
      </div>
    `;
  }

  renderEmailCard() {
    const { confirmation, email, email_failed, principals, terms_accepted } =
      this.item;
    return html`
      <div class="col">
        <div class="card">
          <div class="card-header hstack gap-3">
            <h5 class="mb-0 me-auto">Email</h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              ?disabled=${this.updating}
              @click=${this.onChangeEmail}
            >
              Change
            </button>
            ${!terms_accepted && !confirmation && !principals
              ? html`
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    ?disabled=${this.updating}
                    @click=${this.onInviteUser}
                  >
                    <i class="bi bi-envelope me-1"></i>
                    Invite
                  </button>
                `
              : nothing}
          </div>
          <div class="card-body hstack">
            <a class="text-decoration-none" href="mailto:${email}">${email}</a>
            ${email_failed
              ? html`<div class="badge text-bg-danger ms-auto">Failed</div>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  onResetToken() {
    this.emit('modal:confirm', {
      description: `Do you really want to reset the token for this account?`,
      confirm: async () => {
        await this.resetToken();
      }
    });
  }

  async resetToken() {
    this.updating = true;
    const new_item = await request(
      'PATCH',
      `/connect-accounts/${this.item.account}/reset-token`
    );
    Object.assign(this.item, new_item);
    this.updating = false;
  }

  renderPasswordCard() {
    return html`
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card">
          <div class="card-header hstack">
            <h5 class="mb-0 me-auto">Password</h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              ?disabled=${this.updating}
              @click=${this.onChangePassword}
            >
              Change
            </button>
          </div>
          <div class="card-body">
            ${!this.item.has_password
              ? html`<strong>No password set</strong>`
              : this.item.password
                ? html`<shared-copy-id
                    id=${this.item.password}
                  ></shared-copy-id>`
                : '•••••'}
          </div>
        </div>
      </div>
    `;
  }

  onChangePassword() {
    const { account } = this.item;
    this.emit(
      'modal:open',
      html`
        <modal-connect-account-password
          account="${account}"
          @updated=${(e) => {
            Object.assign(this.item, e.detail);
            this.requestUpdate();
          }}
        ></modal-connect-account-password>
      `
    );
  }

  renderDeliveriesCard() {
    return html`
      <div class="col-12 col-md-6 col-xl-8">
        <div class="card">
          <div class="card-header hstack gap-3">
            <h5 class="mb-0 me-auto">Deliveries</h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click=${this.onDeliveries}
            >
              Update
            </button>
          </div>
          <div class="card-body">${mapDeliveryDetails(this.item.settings)}</div>
        </div>
      </div>
    `;
  }

  onDeliveries() {
    this.emit(
      'modal:open',
      html`
        <modal-connect-account-deliveries
          .account="${this.item}"
          @updated=${(e) => {
            Object.assign(this.item, e.detail);
            if (!e.detail.settings) {
              delete this.item.settings;
            }
            this.requestUpdate();
          }}
        ></modal-connect-account-deliveries>
      `
    );
  }

  renderSettingsCard() {
    const settings = this.item.settings || {};
    return html`
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card">
          <div class="card-header hstack gap-3">
            <h5 class="mb-0 me-auto">Order Settings</h5>
          </div>
          <ul class="list-group list-group-flush py-1">
            ${order_settings.map(
              (setting) => html`
                <li class="list-group-item">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      value="${setting.id}"
                      id="${setting.id}"
                      ?checked=${settings[setting.id] === true}
                      ?disabled=${this.updating}
                      @change=${() => this.onToggleSetting(setting.id)}
                    />
                    <label class="form-check-label" for="${setting.id}">
                      ${setting.name}
                    </label>
                  </div>
                </li>
              `
            )}
          </ul>
        </div>
      </div>
    `;
  }

  async onToggleSetting(setting_id) {
    const settings = this.item.settings || (this.item.settings = {});
    settings[setting_id] = !settings[setting_id];

    this.updating = true;
    const new_item = await request(
      'PATCH',
      `/connect-accounts/${this.item.account}`,
      { [setting_id]: settings[setting_id] }
    );
    Object.assign(this.item, new_item);
    this.updating = false;
  }

  renderFlushCachesCard() {
    return html`
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card">
          <div class="card-header hstack gap-3">
            <h5 class="my-1 me-auto">Flush Caches</h5>
          </div>
          <ul class="list-group list-group-flush py-1">
            <li class="list-group-item hstack">
              <div class="me-auto" alt="scope">Connect Products</div>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                ?disabled=${this.updating}
                @click=${() => this.onFlushCaches()}
              >
                Flush
              </button>
            </li>
          </ul>
        </div>
      </div>
    `;
  }

  onFlushCaches() {
    this.emit('modal:confirm', {
      description: `Do you really want to flush the products cache for this account?`,
      confirm: async () => {
        this.updating = true;
        await request(
          'DELETE',
          `/connect-accounts/${this.item.account}/caches`
        );
        this.updating = false;
      }
    });
  }

  renderLanguageCard() {
    const lang = this.item.lang || 'de';
    return html`
      <div class="col-12 col-md-6 col-xl-4">
        <div class="card">
          <div class="card-header hstack">
            <h5 class="mb-0 me-auto">Language</h5>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              ?disabled=${this.updating}
              @click=${this.onChangeLanguage}
            >
              Change
            </button>
          </div>
          <div class="card-body">${lang}</div>
        </div>
      </div>
    `;
  }

  onChangeLanguage() {
    this.emit(
      'modal:open',
      html`
        <modal-connect-account-language
          .account=${this.item}
          @updated=${(e) => {
            Object.assign(this.item, e.detail);
            this.requestUpdate();
          }}
        ></modal-connect-account-language>
      `
    );
  }

  onChangeEmail() {
    const { account, email } = this.item;
    this.emit(
      'modal:open',
      html`
        <modal-connect-account-email
          account="${account}"
          email="${email}"
          action="update"
          @updated=${(e) => {
            Object.assign(this.item, e.detail);
            this.requestUpdate();
          }}
        ></modal-connect-account-email>
      `
    );
  }

  onInviteUser() {
    const { account, email } = this.item;
    this.emit(
      'modal:open',
      html`
        <modal-connect-account-email
          account="${account}"
          email="${email}"
          action="invite"
          @updated=${(e) => {
            Object.assign(this.item, e.detail);
            this.requestUpdate();
          }}
        ></modal-connect-account-email>
      `
    );
  }

  async onLogin() {
    const { account, email } = this.item;
    const { token } = await request(
      'POST',
      `/connect-accounts/${account}/login`,
      {}
    );
    const hostname = location.hostname
      .replace('admin.', 'connect.')
      .replace('localhost', 'localhost:5173');
    window.open(
      `${location.protocol}//${hostname}/app/login/token/${token}?email=${email}`
    );
  }
}

customElements.define('connect-account-status', ConnectAccountStatus);
